import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';

const ADMIN_TOKEN_KEY = 'metalbox_admin_token';

const TeamMembersManagement = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [currentMember, setCurrentMember] = useState(null); // null for new, object for edit
    const [formData, setFormData] = useState({
        name_zh: '',
        name_en: '',
        title_zh: '',
        title_en: '',
        display_order: 0,
        is_active: true,
        avatar: ''
    });
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [confirmDialog, setConfirmDialog] = useState(null);

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            const response = await axios.get('/api/team-members');
            if (response.data.success) {
                setMembers(response.data.members);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            setMessage({ type: 'error', text: '获取团队成员失败' });
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        const origin = window.location.origin.replace('admin.', '');
        return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const handleOpenModal = (member = null) => {
        if (member) {
            setCurrentMember(member);
            setFormData({
                name_zh: member.name_zh,
                name_en: member.name_en,
                title_zh: member.title_zh,
                title_en: member.title_en,
                display_order: member.display_order,
                is_active: member.is_active,
                avatar: member.avatar
            });
        } else {
            setCurrentMember(null);
            setFormData({
                name_zh: '',
                name_en: '',
                title_zh: '',
                title_en: '',
                display_order: members.length + 1,
                is_active: true,
                avatar: ''
            });
        }
        setShowModal(true);
        setMessage({ type: '', text: '' });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentMember(null);
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (currentMember) {
            // Direct upload for existing member
            const formData = new FormData();
            formData.append('image', file);
            setUploadingAvatar(true);
            try {
                const token = localStorage.getItem(ADMIN_TOKEN_KEY);
                const response = await axios.post(`/api/admin/team-members/${currentMember.id}/upload-avatar`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                });
                if (response.data.success) {
                    setFormData(prev => ({ ...prev, avatar: response.data.imagePath }));
                    // Update list immediately
                    setMembers(prev => prev.map(m => m.id === currentMember.id ? { ...m, avatar: response.data.imagePath } : m));
                }
            } catch (error) {
                console.error('Upload error:', error);
                alert('头像上传失败');
            } finally {
                setUploadingAvatar(false);
            }
        } else {
            // For new member, just preview locally and store file
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, avatarPreview: reader.result, avatarFile: file }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem(ADMIN_TOKEN_KEY);

        try {
            let memberId;
            if (currentMember) {
                // Update
                await axios.put(`/api/admin/team-members/${currentMember.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                memberId = currentMember.id;
            } else {
                // Create
                const response = await axios.post('/api/admin/team-members', formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                memberId = response.data.memberId;
            }

            // Handle avatar upload for new member if file exists
            if (!currentMember && formData.avatarFile) {
                const uploadData = new FormData();
                uploadData.append('image', formData.avatarFile);
                await axios.post(`/api/admin/team-members/${memberId}/upload-avatar`, uploadData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                });
            }

            fetchMembers();
            handleCloseModal();
            setMessage({ type: 'success', text: currentMember ? '更新成功' : '创建成功' });
        } catch (error) {
            console.error('Submit error:', error);
            setMessage({ type: 'error', text: '操作失败: ' + (error.response?.data?.message || error.message) });
        }
    };

    const handleDelete = async (id) => {
        setConfirmDialog({
            message: '确定要删除这位团队成员吗？',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
                    await axios.delete(`/api/admin/team-members/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setMembers(prev => prev.filter(m => m.id !== id));
                    setMessage({ type: 'success', text: '删除成功' });
                } catch (error) {
                    console.error('Delete error:', error);
                    setMessage({ type: 'error', text: '删除失败' });
                } finally {
                    setConfirmDialog(null);
                }
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    if (loading) return (
        <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f5a623]"></div>
        </div>
    );

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white">团队成员管理</h2>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                    </svg>
                    添加成员
                </button>
            </div>

            {message.text && (
                <div className={`mb-6 p-4 rounded-lg border ${message.type === 'success'
                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                    : 'bg-red-500/20 text-red-400 border-red-500/50'
                    }`}>
                    {message.text}
                </div>
            )}

            <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-[#2a2d27]">
                        <thead className="bg-[#262a22]">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">头像</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">姓名</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">职务</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">排序</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">状态</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="bg-[#1a1d17] divide-y divide-[#2a2d27]">
                            {members.map((member) => (
                                <tr key={member.id} className="hover:bg-[#262a22] transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="h-10 w-10 rounded-full overflow-hidden bg-[#262a22] border border-[#2a2d27]">
                                            {member.avatar ? (
                                                <img src={getImageUrl(member.avatar)} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="h-full w-full flex items-center justify-center text-gray-500 text-xs">无</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-white">{member.name_zh}</div>
                                        <div className="text-sm text-gray-500">{member.name_en}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-300">{member.title_zh}</div>
                                        <div className="text-sm text-gray-500">{member.title_en}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                        {member.display_order}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.is_active
                                            ? 'bg-green-500/20 text-green-400'
                                            : 'bg-red-500/20 text-red-400'
                                            }`}>
                                            {member.is_active ? '显示' : '隐藏'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleOpenModal(member)}
                                            className="text-[#4a5f3a] hover:text-[#5a6f4a] font-medium mr-4 transition-colors"
                                        >
                                            编辑
                                        </button>
                                        <button
                                            onClick={() => handleDelete(member.id)}
                                            className="text-red-400 hover:text-red-300 font-medium transition-colors"
                                        >
                                            删除
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-black opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg leading-6 font-medium text-white">
                                        {currentMember ? '编辑成员' : '添加成员'}
                                    </h3>
                                    <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-200 transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                                            <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                                        </svg>
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    {/* Avatar Upload */}
                                    <div className="mb-6 flex justify-center">
                                        <div className="relative h-24 w-24 rounded-full overflow-hidden bg-[#262a22] border border-[#2a2d27] group">
                                            {(formData.avatar || formData.avatarPreview) ? (
                                                <img
                                                    src={formData.avatarPreview || getImageUrl(formData.avatar)}
                                                    alt="Avatar"
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <div className="h-full w-full flex items-center justify-center text-gray-500 text-sm">无头像</div>
                                            )}
                                            <label htmlFor="modal-avatar-upload" className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
                                                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                                </svg>
                                            </label>
                                            <input
                                                type="file"
                                                id="modal-avatar-upload"
                                                className="hidden"
                                                accept="image/*"
                                                onChange={handleAvatarUpload}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">姓名 (中文)</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name_zh}
                                                onChange={e => setFormData({ ...formData, name_zh: e.target.value })}
                                                className="w-full px-3 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Name (English)</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name_en}
                                                onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                                                className="w-full px-3 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">职务 (中文)</label>
                                            <input
                                                type="text"
                                                value={formData.title_zh}
                                                onChange={e => setFormData({ ...formData, title_zh: e.target.value })}
                                                className="w-full px-3 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Title (English)</label>
                                            <input
                                                type="text"
                                                value={formData.title_en}
                                                onChange={e => setFormData({ ...formData, title_en: e.target.value })}
                                                className="w-full px-3 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">显示顺序</label>
                                            <input
                                                type="number"
                                                value={formData.display_order}
                                                onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                            />
                                        </div>
                                        <div className="flex items-center mt-6">
                                            <input
                                                id="is_active"
                                                type="checkbox"
                                                checked={formData.is_active}
                                                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="h-4 w-4 text-[#f5a623] focus:ring-[#f5a623] border-[#2a2d27] rounded bg-[#262a22]"
                                            />
                                            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-300">
                                                显示此成员
                                            </label>
                                        </div>
                                    </div>

                                    <div className="mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <button
                                            type="submit"
                                            className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-[#4a5f3a] text-base font-medium text-white hover:bg-[#5a6f4a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4a5f3a] sm:col-start-2 sm:text-sm transition-colors"
                                        >
                                            保存
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCloseModal}
                                            className="mt-3 w-full inline-flex justify-center rounded-lg border border-[#2a2d27] shadow-sm px-4 py-2 bg-[#262a22] text-base font-medium text-gray-300 hover:bg-[#363a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5a6f4a] sm:mt-0 sm:col-start-1 sm:text-sm transition-colors"
                                        >
                                            取消
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            {confirmDialog && <ConfirmDialog {...confirmDialog} />}
        </div>
    );
};

export default TeamMembersManagement;
